import type { ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/joy';
import ReactMarkdown from 'react-markdown';
import { getApiErrorMessage } from '../../../lib/apiError';

export function TicketSection({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box component="section" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography level="title-md" component="h3" startDecorator={icon}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}
export function TicketError({ error, retry }: { error: unknown; retry?: () => void }) {
  return error ? (
    <Alert
      color="danger"
      role="alert"
      sx={{ overflowWrap: 'anywhere', my: 1 }}
      endDecorator={
        retry ? (
          <Button size="sm" color="danger" onClick={retry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {getApiErrorMessage(
        error,
        error instanceof Error ? error.message : 'This could not be saved. Please try again.',
      )}
    </Alert>
  ) : null;
}
export function TicketMarkdown({ text }: { text: string }) {
  return (
    <Box
      sx={{
        overflowWrap: 'anywhere',
        fontSize: 'sm',
        lineHeight: 1.7,
        '& p': { my: 1, whiteSpace: 'pre-wrap' },
        '& pre': { p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm', overflowX: 'auto' },
        '& code': { fontSize: '0.9em', bgcolor: 'background.level1' },
        '& a': { color: 'primary.plainColor' },
        '& h1, & h2, & h3': { fontSize: 'md', mt: 2 },
      }}
    >
      <ReactMarkdown
        skipHtml
        components={{
          a: (props) => (
            <a href={props.href} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
          img: (props) => <span>{props.alt || 'Image'} (add images using Attach files)</span>,
        }}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );
}
